/**
 * GuardianCompliMind — cross-company chat. Answers are computed
 * deterministically from the in-memory data via `answerCompliMind`.
 * No LLM required for the demo.
 *
 * Each answer streams in (character-by-character) for the feel of a
 * live assistant. Source chips show which companies the answer used.
 */
import { useEffect, useRef, useState } from "react";
import { Sparkles, Send, Lock } from "lucide-react";
import { SUGGESTED_PROMPTS, answerCompliMind } from "@/lib/guardianData";

export default function GuardianCompliMind({ companies, onSelectCompany }) {
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, streaming]);

  const ask = (q) => {
    if (!q.trim() || streaming) return;
    const ts = Date.now();
    const user = { id: `u-${ts}`, role: "user", text: q.trim() };
    const reply = answerCompliMind(q, companies);
    setMessages((prev) => [...prev, user, { id: `a-${ts}`, role: "assistant", text: "", sources: reply.sources, privilegeNote: reply.privilegeNote }]);
    setDraft("");
    streamIn(reply.text);
  };

  const streamIn = (full) => {
    setStreaming(true);
    let i = 0;
    const step = () => {
      i = Math.min(full.length, i + Math.max(1, Math.round(full.length / 80)));
      setMessages((prev) => {
        const next = prev.slice();
        next[next.length - 1] = { ...next[next.length - 1], text: full.slice(0, i) };
        return next;
      });
      if (i < full.length) setTimeout(step, 22);
      else setStreaming(false);
    };
    step();
  };

  return (
    <div className="p-6 md:p-8 flex flex-col h-full" style={{ minHeight: 600 }}>
      <div>
        <div className="text-[9px] mb-1" style={{ fontFamily: "ui-monospace, SF Mono, monospace", letterSpacing: "0.18em", color: "rgba(15,30,46,0.5)" }}>/ COMPLIMIND · PORTFOLIO MODE</div>
        <h2 className="text-2xl font-semibold flex items-center gap-2" style={{ color: "#0F1E2E" }}><Sparkles className="h-5 w-5" style={{ color: "#F5B400" }} /> Ask across every company</h2>
        <p className="text-sm text-ink-900/65 mt-1">
          Answers computed from live portfolio data. <strong className="text-ink-900/85">Compliance posture only — privileged legal content is walled off.</strong>
        </p>
      </div>

      {messages.length === 0 && (
        <div className="mt-6">
          <div className="text-[10px] mb-2" style={{ fontFamily: "ui-monospace, SF Mono, monospace", letterSpacing: "0.18em", color: "rgba(15,30,46,0.5)" }}>/ TRY ONE</div>
          <div className="flex flex-wrap gap-2" data-testid="cm-prompts">
            {SUGGESTED_PROMPTS.map((p) => (
              <button key={p} onClick={() => ask(p)} className="text-xs px-3 py-1.5 rounded-full border hover:bg-cream-50 transition" style={{ borderColor: "rgba(15,30,46,0.15)", color: "#0F1E2E" }}>
                {p}
              </button>
            ))}
          </div>
        </div>
      )}

      <div ref={scrollRef} className="flex-1 my-4 overflow-y-auto pr-1 space-y-3" data-testid="cm-messages">
        {messages.map((m) => (
          <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div
              className="max-w-[85%] rounded-md p-3 text-sm whitespace-pre-wrap"
              style={{
                background: m.role === "user" ? "#0F1E2E" : "rgba(245,180,0,0.10)",
                color: m.role === "user" ? "#FDFBF5" : "#0F1E2E",
                border: m.role === "user" ? "none" : "1px solid rgba(245,180,0,0.30)",
              }}
            >
              <div>{m.text}{m.role === "assistant" && streaming && m.id === messages[messages.length - 1]?.id && <span className="inline-block w-1.5 h-3 ml-1 align-baseline animate-pulse" style={{ background: "#0F1E2E" }} />}</div>
              {m.role === "assistant" && m.sources && m.sources.length > 0 && (
                <div className="mt-3 pt-2 border-t flex flex-wrap gap-1.5" style={{ borderColor: "rgba(15,30,46,0.12)" }}>
                  <span className="text-[9px]" style={{ fontFamily: "ui-monospace, SF Mono, monospace", letterSpacing: "0.18em", color: "rgba(15,30,46,0.50)" }}>SOURCES:</span>
                  {m.sources.map((id) => {
                    const co = companies.find((c) => c.id === id);
                    return co ? (
                      <button key={id} onClick={() => onSelectCompany(id)}
                              className="text-[10px] px-2 py-0.5 rounded-full border hover:bg-sun-500/10"
                              style={{ borderColor: "rgba(245,180,0,0.5)", color: "#a16207", fontFamily: "ui-monospace, SF Mono, monospace", letterSpacing: "0.10em" }}
                              data-testid={`cm-source-${id}`}>
                        {co.name}
                      </button>
                    ) : null;
                  })}
                </div>
              )}
              {m.role === "assistant" && m.privilegeNote && (
                <div className="mt-2 text-[10px] inline-flex items-center gap-1" style={{ color: "rgba(15,30,46,0.55)" }}>
                  <Lock className="h-2.5 w-2.5" /> {m.privilegeNote}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      <form
        onSubmit={(e) => { e.preventDefault(); ask(draft); }}
        className="flex gap-2"
      >
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Ask CompliMind about the portfolio…"
          className="flex-1 px-3 py-2 border rounded-md text-sm outline-none focus:border-sun-500"
          style={{ borderColor: "rgba(15,30,46,0.15)" }}
          data-testid="cm-input"
        />
        <button
          type="submit"
          disabled={streaming || !draft.trim()}
          className="px-4 py-2 rounded-md text-xs font-medium inline-flex items-center gap-1 disabled:opacity-50"
          style={{ background: "#0F1E2E", color: "#FDFBF5", fontFamily: "ui-monospace, SF Mono, monospace", letterSpacing: "0.14em" }}
          data-testid="cm-send"
        >
          <Send className="h-3 w-3" /> ASK
        </button>
      </form>
    </div>
  );
}
